import { Component, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit, OnDestroy, Injector, ElementRef, ViewChild } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { AuthRole } from '@cf-shared/couch-auth';
import { AuthService } from 'services/auth.service';
import { waitForAngularUpdate } from 'services/browser-utils';
import { sleep } from '@cf-shared/shared-util';
import { DateTime } from 'luxon';
import { CsScreenshotAnnotatorComponent } from 'components/cs-screenshot-annotator/cs-screenshot-annotator';

interface TestUiComponentsPageParams {
  // a card's data-card-id or an example row's data-showcase-id to scroll to
  scrollCard?: string;
}

// one entry of the index card: a card, or an example row inside it
interface ShowcaseIndexEntry {
  id: string;
  label: string;
}

interface ShowcaseIndexCard extends ShowcaseIndexEntry {
  rows: ShowcaseIndexEntry[];
}

// the elements a scrollCard param can point at, in page order
const SCROLL_TARGET_SELECTOR = 'cs-card[data-card-id], [data-showcase-id]';

// The Test UI Components page: the index card, direct links (scrollCard) and the screenshot
// annotator overlay. Every other card's body is its own cs-test-*-card component beside this file,
// holding that card's markup, sample data and handlers; the page template keeps only each card's
// cs-card shell (see the comment there for why).
@Component({
    selector: 'page-test-ui-components',
    templateUrl: 'test-ui-components.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TestUiComponentsPage extends CsPage<TestUiComponentsPageParams> implements AfterViewInit, OnDestroy {
  // sample values two cards share, so typing or picking in one shows in the other as well:
  // the text of Control Outlines' rest-state cs-input and Layout's cs-form-field, and the date of
  // Control Outlines' input-calendar and the Calendar card's "Valid" field
  sampleText = '';
  sampleDate = DateTime.local().toISO();

  showAnnotator = false;
  readonly annotatorImage = 'assets/imgs/default-avatar.jpg';

  // Scroll tracking
  private scrollTimeout?: ReturnType<typeof setTimeout>;
  private scrollContainer?: Element;
  private shouldScrollToCard = false;
  // kept so the same function can be removed again
  private readonly scrollListener = () => this.onScroll();

  // filled from the page itself after the view renders, so it lists every card and row without a hand-kept copy
  showcaseIndex: ShowcaseIndexCard[] = [];

  // the annotator exists only while showAnnotator is true
  @ViewChild('annotator') private annotator?: CsScreenshotAnnotatorComponent;

  constructor(
    public navCtrl: NavController,
    protected auth: AuthService,
    private cd: ChangeDetectorRef,
    private injector: Injector,
    private elementRef: ElementRef,
  ) {
    super();
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Unknown);
  }

  async ionViewWillEnter() {
    // if scrollCard param exists, scroll to it after view init
    if (this.navParams.data.scrollCard) {
      this.shouldScrollToCard = true;
    }

    this.cd.markForCheck();
  }

  async ngAfterViewInit() {
    // wait for DOM to be ready
    await waitForAngularUpdate(this.injector);

    // find scrollable container within this component
    const nativeElement = this.elementRef.nativeElement;
    this.scrollContainer = nativeElement.querySelector('.cs-scrollable-area');

    if (this.scrollContainer) {
      this.scrollContainer.addEventListener('scroll', this.scrollListener, { passive: true });
    }

    if (this.viewDestroyed) {
      return;
    }
    this.showcaseIndex = this.buildShowcaseIndex();
    this.cd.markForCheck();

    // if we have a scrollCard param, scroll to it after layout completes
    if (this.shouldScrollToCard && this.navParams.data.scrollCard) {
      await sleep(500);
      this.scrollToCard(this.navParams.data.scrollCard);
      this.shouldScrollToCard = false;
    }
  }

  ngOnDestroy() {
    // cleanup scroll listener
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.scrollListener);
    }
    if (this.scrollTimeout) {
      this.cancelTimeout(this.scrollTimeout);
    }
  }

  private onScroll() {
    // if the page is leaving or covered, a scroll no longer describes the current page. the URL
    // write goes to whichever page is active, so it must not land on the page that replaced this one
    if (this.viewDestroyed) {
      return;
    }
    // debounce: wait 1 second after scrolling stops. safeTimeout is cancelled when the page leaves
    if (this.scrollTimeout) {
      this.cancelTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = this.safeTimeout(() => this.checkVisibleCard(), 1000);
  }

  private checkVisibleCard() {
    // find the last card or example row whose top has reached the top of the scroll container
    const nativeElement = this.elementRef.nativeElement;
    const cards = Array.from(nativeElement.querySelectorAll(SCROLL_TARGET_SELECTOR)) as HTMLElement[];

    if (!this.scrollContainer) {
      return;
    }

    // get scroll container's position
    const containerRect = this.scrollContainer.getBoundingClientRect();
    // small, so a card's first row does not win over the card itself right after jumping to the card
    const threshold = 24; // pixels from top of container

    // find the first card whose top is visible or just above the threshold
    let visibleCard: HTMLElement | null = null;
    for (const card of cards) {
      const cardRect = card.getBoundingClientRect();
      // calculate card position relative to container top
      const cardTopRelative = cardRect.top - containerRect.top;

      if (cardTopRelative <= threshold) {
        visibleCard = card;
      } else {
        break; // cards are in order, so we can stop
      }
    }

    // use the last card we found
    if (visibleCard) {
      const cardId = this.scrollTargetId(visibleCard);
      if (cardId) {
        void this.updateScrollCardParam(cardId);
      }
    }
  }

  // jump from the index card to a card or example row, and put it in the URL so the link can be shared
  jumpToShowcase(id: string) {
    this.scrollToCard(id);
    void this.updateScrollCardParam(id);
  }

  // the id a scroll target is linked by: a row's data-showcase-id, else a card's data-card-id
  private scrollTargetId(element: Element): string | null {
    return element.getAttribute('data-showcase-id') ?? element.getAttribute('data-card-id');
  }

  // read every card and its example rows from the rendered page, leaving out the index card itself
  private buildShowcaseIndex(): ShowcaseIndexCard[] {
    const nativeElement: HTMLElement = this.elementRef.nativeElement;
    const cards = Array.from(nativeElement.querySelectorAll('cs-card[data-card-id]'));
    return cards
      .filter(card => card.getAttribute('data-card-id') !== 'index')
      .map(card => {
        const id = card.getAttribute('data-card-id') ?? '';
        // a card is named by its header; the older cards have only a list header, and a few have neither
        // (the header sits inside the card's rendered wrapper, so it is not a direct child)
        const heading = card.querySelector('cs-card-header') ?? card.querySelector('cs-item-list-header');
        const rows = Array.from(card.querySelectorAll('[data-showcase-id]')).map(row => ({
          id: row.getAttribute('data-showcase-id') ?? '',
          label: row.querySelector(':scope > cs-label')?.textContent?.trim() || (row.getAttribute('data-showcase-id') ?? ''),
        }));
        return { id, label: heading?.textContent?.trim() || id, rows };
      });
  }

  private scrollToCard(cardId: string) {
    // find the card or example row and scroll to it within this component
    const nativeElement = this.elementRef.nativeElement;
    const escapedId = CSS.escape(cardId);
    const card = nativeElement.querySelector(`cs-card[data-card-id="${escapedId}"], [data-showcase-id="${escapedId}"]`);
    if (card) {
      // use scrollIntoView with smooth behavior
      card.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }

  private async updateScrollCardParam(cardId: string) {
    // update URL with scrollCard param
    const params: TestUiComponentsPageParams = {
      scrollCard: cardId
    };

    await this.navCtrl.updateCurrentPageParamsAsync(params);
    this.cd.markForCheck();
  }

  // opens or closes the full-screen annotator. like its real hosts (send-feedback,
  // send-coach-message), the page starts it once it is on screen: initAfterEnter() draws the
  // image and attaches the pointer listeners, which the annotator does not do by itself
  async toggleAnnotator(visible: boolean) {
    this.showAnnotator = visible;
    this.cd.markForCheck();
    if (!visible) return;
    await waitForAngularUpdate(this.injector);
    if (this.viewDestroyed) return;
    this.annotator?.initAfterEnter();
  }
}
